import PropTypes from "prop-types";
import React, { useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";

import FAQSearch from "./FAQSearch";

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Title = styled.h1`
    color: var(--navy);
    text-align: center;
    margin-bottom: 16px;
    font-size: 2.5rem;
    font-weight: 700;
`;

const Subtitle = styled.p`
    color: var(--navy);
    text-align: center;
    margin-bottom: 32px;
    font-size: 1.125rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
`;

const ResultsCount = styled.div`
    text-align: center;
    color: var(--navy);
    margin: 16px 0;
    font-style: italic;
    font-size: 0.95rem;
`;

const PaginationControls = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    gap: 10px;

    button {
        background-color: var(--navy);
        color: white;
        border: none;
        padding: 10px 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 50%;
        font-size: 16px;
        width: 50px;
        height: 50px;
        transition: background 0.3s ease;

        &:disabled {
            background-color: #ccc;
            cursor: not-allowed;
        }
    }
`;
const ContactUsNote = styled.div`
    text-align: center;
    margin-top: 40px;
    font-size: 1rem;
    color: #333;

    a {
        color: var(--navy);
        font-weight: 600;
        text-decoration: underline;

        &:hover {
            color: #006699;
        }
    }
`;

const FAQContainer = styled.div`
    background-color: #F5F7FA;
    border-left: 5px solid var(--navy);
    border-radius: 12px;
    margin-bottom: 16px;
    padding: 18px 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.3s ease-in-out;

    &:hover {
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
    }
`;

const Question = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--navy);
    transition: color 0.3s;

    &:hover {
        color: #006699;
    }
`;

const Answer = styled.div`
    margin-top: 12px;
    padding-left: 10px;
    color: #333;
    font-size: 0.95rem;
    line-height: 1.5;
    animation: fadeIn 0.3s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-4px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

function FAQItem({ question, answer, isOpen, onToggle }) {
    return (
        <FAQContainer>
            <Question onClick={onToggle}>
                {question}
                <span style={{ fontSize: "1.2rem", color: "var(--navy)" }}>
                    {isOpen ? "−" : "+"}
                </span>
            </Question>
            {isOpen && (
                <Answer>
                    {answer}
                    {/* <div>
            <small>Created by: {createdBy} on {new Date(createdOn).toLocaleDateString()}</small>
            <br />
            <small>Updated by: {updatedBy} on {new Date(updatedOn).toLocaleDateString()}</small>
          </div> */}
                </Answer>
            )}
        </FAQContainer>
    );
}

FAQItem.propTypes = {
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

export default function FAQList({ faqs, title, subtitle }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [openFAQId, setOpenFAQId] = useState(null);
    const itemsPerPage = 5;

    const filteredFAQs = faqs.filter(
        (faq) =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const totalPages = Math.ceil(filteredFAQs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentFAQs = filteredFAQs.slice(
        startIndex,
        startIndex + itemsPerPage,
    );

    const handleNextPage = () => {
        if(currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if(currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const toggleFAQ = (id) => {
        setOpenFAQId((prevId) => (prevId === id ? null : id));
    };

    return (
        <Container>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}

            <FAQSearch
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                }}
            />

            {searchTerm && (
                <ResultsCount>
                    Showing {currentFAQs.length} of {filteredFAQs.length} FAQs
                </ResultsCount>
            )}

            {currentFAQs.length > 0 ? (
                currentFAQs.map((faq) => (
                    <FAQItem
                        key={faq.id}
                        question={faq.question}
                        answer={faq.answer}
                        createdBy={faq.audit_created_by}
                        createdOn={faq.audit_created_on}
                        updatedBy={faq.audit_updated_by}
                        updatedOn={faq.audit_updated_on}
                        isOpen={openFAQId === faq.id}
                        onToggle={() => toggleFAQ(faq.id)}
                    />
                ))
            ) : (
                <ResultsCount>No FAQs match your search.</ResultsCount>
            )}

            {totalPages > 1 && (
                <PaginationControls>
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        <FaArrowLeft />
                    </button>
                    <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        <FaArrowRight />
                    </button>
                </PaginationControls>
            )}

            <ContactUsNote>
                If you have more queries, then{" "}
                <Link to={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/contact-us`}>Contact Us</Link>.
            </ContactUsNote>
        </Container>
    );
}

FAQList.propTypes = {
    faqs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            question: PropTypes.string.isRequired,
            answer: PropTypes.string.isRequired,
            audit_created_by: PropTypes.string,
            audit_created_on: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.instanceOf(Date),
            ]),
            audit_updated_by: PropTypes.string,
            audit_updated_on: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.instanceOf(Date),
            ]),
        }),
    ).isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
};
